import React from 'react';
import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users,
  MessageSquare,
  BarChart3,
  FileText,
  FileSearch,
  User,
  Building2,
  ClipboardList,
  LineChart,
  GraduationCap,
  Settings,
  MapPin,
  Shield,
  ShieldAlert,
} from 'lucide-react';
import owlSvg from '@/assets/images/owl.svg';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

// Tour anchor attributes: path → data-tour value (Spec 018)
const TOUR_ATTRS: Record<string, string> = {
  '/stakeholders': 'stakeholders-nav',
  '/progress': 'assessment-link',
};

export const Sidebar: React.FC = () => {
  const { userClaims, userProfile } = useAuth();
  const userRole = userClaims?.role || 'student';
  const isSuperAdmin = userClaims?.isSuperAdmin || false;

  const allNavItems = [
    { path: '/stakeholders', label: 'Stakeholders', icon: Users, roles: ['admin', 'instructor', 'student'] },
    { path: '/interview', label: 'Interview', icon: MessageSquare, roles: ['admin', 'instructor', 'student'] },
    { path: '/progress', label: 'Progress', icon: BarChart3, roles: ['admin', 'instructor', 'student'] },
    { path: '/artifacts', label: 'Artifacts', icon: FileSearch, roles: ['admin', 'instructor', 'student'] },
    // Threat Modeling: TM module content has shipped (spec 026 visual layer, Task 2) — students
    // now have access alongside admin/instructor. The bare /threat-modeling route still lands on
    // a landing page explaining a module id is required.
    { path: '/threat-modeling', label: 'Threat Modeling', icon: Shield, roles: ['admin', 'instructor', 'student'] },
    { path: '/report', label: 'Report', icon: FileText, roles: ['admin', 'instructor', 'student'] },
    { path: '/instructor', label: 'Instructor', icon: BarChart3, roles: ['admin', 'instructor'] },
    { path: '/threat-modeling/adjudication', label: 'Threat Adjudication', icon: ShieldAlert, roles: ['admin', 'instructor'] },
    { path: '/users', label: 'Users', icon: User, roles: ['admin', 'instructor'] },
  ];

  const adminNavItems = [
    { path: '/admin', label: 'Admin Portal', icon: Settings },
    { path: '/admin/courses', label: 'Courses', icon: GraduationCap },
  ];

  const superAdminNavItems = [
    { path: '/super-admin/tenants', label: 'Tenants', icon: Building2 },
    { path: '/super-admin/audit-logs', label: 'Audit Logs', icon: ClipboardList },
    { path: '/super-admin/analytics', label: 'Analytics', icon: LineChart },
  ];

  const navItems = allNavItems.filter(item => item.roles.includes(userRole));

  // Token `name` claim covers the window before the backend profile loads (and OAuth
  // accounts whose profile row still has a stale email-localpart display name).
  const displayName = userProfile?.displayName || userClaims?.name || userClaims?.email || 'User';
  const initials = displayName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="fixed left-0 top-0 h-full w-64 bg-card border-r border-border flex flex-col">
      {/* Brand mark — owl + scenario context */}
      <div className="p-4 border-b border-border flex items-center gap-3">
        <img
          src={owlSvg}
          alt="Luminex Learning"
          style={{
            height: '48px',
            width: 'auto',
            filter: 'var(--owl-filter-gold)',
            flexShrink: 0,
          }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1 }}>DigiStore</span>
          <div style={{ height: '1px', background: 'var(--color-border-primary)' }} />
          <span style={{ fontSize: '10px', fontWeight: 500, color: 'var(--color-text-secondary)', lineHeight: 1 }}>GRC Labs</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {/* Main Navigation */}
        <ul className="space-y-1 px-3">
          {navItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                className={({ isActive }) => 'block relative'}
              >
                {({ isActive }) => (
                  <div
                    data-tour={TOUR_ATTRS[item.path]}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors relative",
                      isActive
                        ? "text-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="sidebar-active"
                        className="absolute inset-0 bg-primary/10 rounded-lg"
                        transition={{ type: "spring", stiffness: 350, damping: 30 }}
                      />
                    )}
                    <item.icon className="h-5 w-5 relative z-10" />
                    <span className="relative z-10">{item.label}</span>
                  </div>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Admin Navigation */}
        {userRole === 'admin' && (
          <>
            <div className="px-6 py-3 mt-4">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Administration
              </div>
            </div>
            <ul className="space-y-1 px-3">
              {adminNavItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => 'block relative'}
                  >
                    {({ isActive }) => (
                      <div
                        className={cn(
                          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors relative",
                          isActive
                            ? "text-primary"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        )}
                      >
                        {isActive && (
                          <motion.div
                            layoutId="sidebar-admin-active"
                            className="absolute inset-0 bg-primary/10 rounded-lg"
                            transition={{ type: "spring", stiffness: 350, damping: 30 }}
                          />
                        )}
                        <item.icon className="h-5 w-5 relative z-10" />
                        <span className="relative z-10">{item.label}</span>
                      </div>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Super Admin Navigation */}
        {isSuperAdmin && (
          <>
            <div className="px-6 py-3 mt-4">
              <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>
                Super Admin
              </div>
            </div>
            <ul className="space-y-1 px-3">
              {superAdminNavItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => 'block relative'}
                  >
                    {({ isActive }) => (
                      <div
                        className={cn(
                          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors relative",
                          isActive
                            ? "text-primary"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        )}
                      >
                        {isActive && (
                          <motion.div
                            layoutId="sidebar-super-active"
                            className="absolute inset-0 bg-primary/10 rounded-lg"
                            transition={{ type: "spring", stiffness: 350, damping: 30 }}
                          />
                        )}
                        <item.icon className="h-5 w-5 relative z-10" />
                        <span className="relative z-10">{item.label}</span>
                      </div>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </>
        )}
      </nav>

      {/* Take the Tour Again — Spec 018 FR-008, FR-009 */}
      {userRole === 'student' && (
        <div className="px-3 pb-2">
          <button
            id="sidebar-take-tour"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            onClick={() => window.dispatchEvent(new CustomEvent('orientation:restart'))}
          >
            <MapPin className="h-5 w-5" />
            <span>Take the tour again</span>
          </button>
        </div>
      )}

      {/* User Card */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-primary/10 text-primary text-xs">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
            <p className="text-xs text-muted-foreground capitalize">{userRole}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
